import { isSupabaseConfigured, missingSupabaseConfig } from "./config";
import { supabase } from "./client";
import { withRequestDeadline } from "./requestDeadline";
import { invalidateMenuProductImage } from "./menuProductImages";
import { optimizeImageFile } from "../images/optimizeImage";
import {
  buildCategoryIcon,
  DEFAULT_MENU_CATEGORIES,
} from "../menu/defaultMenuConfig";
import {
  DEFAULT_STOCK_UNIT,
  normalizeStockUnit,
} from "../local/stockUnits";
import {
  getIngredientConsumptionQuantity,
  getIngredientMenuBaseQuantity,
} from "../local/menuIngredientModel";
import { normalizePizzaSizeOptions } from "../local/pizzaHalfAndHalf";
import {
  normalizeEmpanadaCookingOptions,
  normalizeEmpanadaSaleOptions,
} from "../local/empanadaSales";

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      `Faltan variables de entorno para Supabase: ${missingSupabaseConfig.join(", ")}`
    );
  }
};

const normalizeError = (error, fallbackMessage) => {
  if (!error) {
    return new Error(fallbackMessage);
  }

  return new Error(error.message || fallbackMessage);
};

const isStatementTimeoutError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return error?.code === "57014" || message.includes("statement timeout");
};

const isMissingFunctionError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("could not find the function")
  );
};

const isMissingRelationError = (error, relationName) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();
  const safeRelationName = String(relationName || "").toLowerCase();

  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    message.includes(safeRelationName) ||
    message.includes(`relation "public.${safeRelationName}" does not exist`)
  );
};

const isMissingColumnError = (error, columnName) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();
  const safeColumnName = String(columnName || "").toLowerCase();

  return (
    error?.code === "42703" ||
    message.includes(`column "${safeColumnName}" does not exist`) ||
    message.includes(`column ${safeColumnName} does not exist`) ||
    message.includes(safeColumnName)
  );
};

const isMissingProductChannelSettingsTableError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    message.includes(
      'relation "public.external_product_channel_settings" does not exist'
    ) ||
    message.includes("could not find the table 'public.external_product_channel_settings'")
  );
};

const isMenuNameConstraintError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "23505" &&
    (message.includes("menus_company_name_uidx") ||
      message.includes("duplicate key value violates unique constraint"))
  );
};

const ensureMenuId = (menuId) => {
  const safeMenuId = String(menuId || "").trim();

  if (!safeMenuId) {
    throw new Error(
      "Todavia no encontramos un menu activo para esta sucursal. Recarga la pantalla e intentalo de nuevo."
    );
  }

  return safeMenuId;
};

const normalizePriceMode = (value) => {
  if (value === "manual") {
    return "manual";
  }

  if (value === "suggested" || value === "sugerido") {
    return "suggested";
  }

  return "suggested";
};

const DEFAULT_MAX_EXTRA_QUANTITY = 2;
const DELIVERY_MENU_CHANNEL = "menu_web";
let productIngredientUnitSupportChecked = false;
let productIngredientMenuQuantitySupportChecked = false;
let productPizzaSizeOptionsSupportChecked = false;
let productEmpanadaSaleOptionsSupportChecked = false;
let productEmpanadaCookingOptionsSupportChecked = false;

const recipeRowsMatch = (current, next) => {
  if (!current) {
    return false;
  }

  const currentMenuQuantity = Number(
    current.menu_quantity ?? current.quantity ?? 0
  );

  return (
    Number(current.quantity || 0) === Number(next.quantity || 0) &&
    currentMenuQuantity === Number(next.menu_quantity || 0) &&
    normalizeStockUnit(current.unit) === normalizeStockUnit(next.unit) &&
    Boolean(current.can_remove) === Boolean(next.can_remove) &&
    Boolean(current.can_add_more) === Boolean(next.can_add_more) &&
    Number(current.max_extra_quantity || 0) ===
      Number(next.max_extra_quantity || 0)
  );
};

const throwRecipeWriteError = (error) => {
  if (isMissingColumnError(error, "unit")) {
    throw new Error(
      "Falta aplicar menu_recipe_stock_units_patch.sql en Supabase para guardar unidades de consumo por ingrediente."
    );
  }

  if (isMissingColumnError(error, "menu_quantity")) {
    throw new Error(
      "Falta aplicar product_recipe_customization_patch.sql en Supabase para separar stock y cantidad visible en el menu."
    );
  }

  throw normalizeError(error, "No se pudieron guardar los ingredientes del plato.");
};

const ensureProductPizzaSizeOptionsSupported = async () => {
  if (productPizzaSizeOptionsSupportChecked) {
    return;
  }

  const { error } = await supabase
    .from("products")
    .select("pizza_size_options")
    .limit(1);

  if (error) {
    if (isMissingColumnError(error, "pizza_size_options")) {
      throw new Error(
        "Falta aplicar product_pizza_size_options_patch.sql en Supabase para guardar tamanos y precios de pizza."
      );
    }

    throw normalizeError(
      error,
      "No se pudo verificar la configuracion de tamanos de pizza."
    );
  }

  productPizzaSizeOptionsSupportChecked = true;
};

const ensureProductEmpanadaSaleOptionsSupported = async () => {
  if (productEmpanadaSaleOptionsSupportChecked) {
    return;
  }

  const { error } = await supabase
    .from("products")
    .select("empanada_sale_options")
    .limit(1);

  if (error) {
    if (isMissingColumnError(error, "empanada_sale_options")) {
      throw new Error(
        "Falta aplicar product_empanada_sale_options_patch.sql en Supabase para guardar precios de empanadas."
      );
    }

    throw normalizeError(
      error,
      "No se pudo verificar la configuracion de presentaciones de empanadas."
    );
  }

  productEmpanadaSaleOptionsSupportChecked = true;
};

const ensureProductEmpanadaCookingOptionsSupported = async () => {
  if (productEmpanadaCookingOptionsSupportChecked) {
    return;
  }

  const { error } = await supabase
    .from("products")
    .select("empanada_cooking_options")
    .limit(1);

  if (error) {
    if (isMissingColumnError(error, "empanada_cooking_options")) {
      throw new Error(
        "Falta aplicar product_empanada_cooking_options_patch.sql en Supabase para guardar cocciones de empanadas."
      );
    }

    throw normalizeError(
      error,
      "No se pudo verificar la configuracion de cocciones de empanadas."
    );
  }

  productEmpanadaCookingOptionsSupportChecked = true;
};

const upsertChangedRecipeRows = async (rows) => {
  if (!rows.length) {
    return;
  }

  const { error } = await supabase
    .from("product_ingredients")
    .upsert(rows, { onConflict: "product_id,ingredient_id" });

  if (!error) {
    return;
  }

  if (!isStatementTimeoutError(error) || rows.length === 1) {
    throwRecipeWriteError(error);
  }

  // A legacy snapshot trigger may rebuild the complete menu once per row.
  // Retrying one row at a time keeps each database statement below its timeout.
  for (const row of rows) {
    const { error: rowError } = await supabase
      .from("product_ingredients")
      .upsert(row, { onConflict: "product_id,ingredient_id" });

    if (rowError) {
      throwRecipeWriteError(rowError);
    }
  }
};

const deleteRemovedRecipeRows = async (productId, ingredientIds) => {
  if (!ingredientIds.length) {
    return;
  }

  const { error } = await supabase
    .from("product_ingredients")
    .delete()
    .eq("product_id", productId)
    .in("ingredient_id", ingredientIds);

  if (!error) {
    return;
  }

  if (!isStatementTimeoutError(error) || ingredientIds.length === 1) {
    throw normalizeError(error, "No se pudo actualizar la receta del plato.");
  }

  for (const ingredientId of ingredientIds) {
    const { error: rowError } = await supabase
      .from("product_ingredients")
      .delete()
      .eq("product_id", productId)
      .eq("ingredient_id", ingredientId);

    if (rowError) {
      throw normalizeError(rowError, "No se pudo actualizar la receta del plato.");
    }
  }
};

const normalizeLinkSelectionMode = () => "single_optional";

const fileToDataUrl = (file) => optimizeImageFile(file, { profile: "product" });

const menuQuery = (query, signal) => {
  if (signal?.aborted) {
    throw Object.assign(new Error("La carga del menu tardo demasiado. Vuelve a intentar."), { code: "REQUEST_TIMEOUT" });
  }
  return signal ? query.abortSignal(signal) : query;
};

const getAssignedMenu = async (branchId, signal) => {
  const { data, error } = await menuQuery(supabase
    .from("branch_menu_assignments")
    .select("menu_id, mode")
    .eq("branch_id", branchId)
    .maybeSingle(), signal);

  if (error && !isMissingRelationError(error, "branch_menu_assignments")) {
    throw normalizeError(error, "No se pudo cargar el menu asignado a la sucursal.");
  }

  if (data?.menu_id) {
    return data;
  }

  const branch = await getBranchById(branchId, signal);

  if (!branch?.company_id) {
    throw new Error("La sucursal activa todavia no tiene un menu asignado.");
  }

  const { data: repairedAssignment, error: repairError } = await menuQuery(supabase.rpc(
    "assign_shared_menu_to_branch",
    {
      p_branch_id: branchId,
    }
  ), signal);

  if (repairError && !isMissingFunctionError(repairError)) {
    throw normalizeError(
      repairError,
      "No se pudo vincular automaticamente el menu compartido a la sucursal."
    );
  }

  if (repairedAssignment?.menu_id) {
    return {
      menu_id: repairedAssignment.menu_id,
      mode: repairedAssignment.mode || "shared",
    };
  }

  const sharedMenu = await getSharedCompanyMenu(branch.company_id, signal);

  if (!error) {
    const { error: upsertError } = await menuQuery(supabase.from("branch_menu_assignments").upsert({
      branch_id: branchId,
      menu_id: sharedMenu.id,
      mode: "shared",
    }), signal);

    if (upsertError && !isMissingRelationError(upsertError, "branch_menu_assignments")) {
      throw normalizeError(
        upsertError,
        "No se pudo vincular el menu compartido a la sucursal."
      );
    }
  }

  return {
    menu_id: sharedMenu.id,
    mode: "shared",
  };
};

const getBranchById = async (branchId, signal) => {
  const { data, error } = await menuQuery(supabase
    .from("branches")
    .select("*")
    .eq("id", branchId)
    .single(), signal);

  if (error) {
    throw normalizeError(error, "No se pudo cargar la sucursal.");
  }

  return data;
};

const getMenuById = async (menuId) => {
  const { data, error } = await supabase
    .from("menus")
    .select("*")
    .eq("id", menuId)
    .single();

  if (error) {
    throw normalizeError(error, "No se pudo cargar el menu.");
  }

  return data;
};

const getSharedCompanyMenu = async (companyId, signal) => {
  const { data, error } = await menuQuery(supabase
    .from("menus")
    .select("*")
    .eq("company_id", companyId)
    .eq("scope", "shared")
    .eq("active", true)
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle(), signal);

  if (error) {
    throw normalizeError(error, "No se pudo cargar el menu compartido.");
  }

  if (!data?.id) {
    throw new Error("La empresa no tiene un menu compartido disponible.");
  }

  return data;
};

const listCategories = async (menuId, signal, includeImages = true) => {
  const safeMenuId = ensureMenuId(menuId);
  const { data, error } = await menuQuery(supabase
    .from("categories")
    .select(includeImages ? "*" : "id, name, icon, sort_order, created_at")
    .eq("menu_id", safeMenuId)
    .eq("active", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true }), signal);

  if (error) {
    throw normalizeError(error, "No se pudieron cargar las categorias del menu.");
  }

  return data || [];
};

const ensureDefaultCategories = async (menuId, signal, includeImages = true) => {
  const safeMenuId = ensureMenuId(menuId);
  const existing = await listCategories(safeMenuId, signal, includeImages);

  if (existing.length > 0) {
    return existing;
  }

  const categoryPayload = DEFAULT_MENU_CATEGORIES.map((category, index) => ({
    menu_id: safeMenuId,
    name: category.name,
    icon: category.icon || buildCategoryIcon(category.name),
    sort_order: index,
    active: true,
  }));

  const { data: insertedCategories, error: categoryError } = await menuQuery(supabase
    .from("categories")
    .insert(categoryPayload)
    .select("*"), signal);

  if (categoryError) {
    throw normalizeError(categoryError, "No se pudieron crear las categorias base.");
  }

  const createdCategories = (insertedCategories || []).sort((left, right) => {
    const sortDiff = Number(left.sort_order || 0) - Number(right.sort_order || 0);

    if (sortDiff !== 0) {
      return sortDiff;
    }

    return new Date(left.created_at || 0) - new Date(right.created_at || 0);
  });
  const subcategoryPayload = createdCategories.flatMap((category, index) => {
    const defaults = DEFAULT_MENU_CATEGORIES[index]?.subcategories || [];

    return defaults.map((subcategoryName, subIndex) => ({
      menu_id: safeMenuId,
      category_id: category.id,
      name: subcategoryName,
      sort_order: subIndex,
      active: true,
    }));
  });

  if (subcategoryPayload.length > 0) {
    const { error: subcategoryError } = await menuQuery(supabase
      .from("subcategories")
      .insert(subcategoryPayload), signal);

    if (subcategoryError) {
      throw normalizeError(
        subcategoryError,
        "No se pudieron crear las subcategorias base."
      );
    }
  }

  return createdCategories;
};

const listSubcategories = async (menuId, signal, lightweight = false) => {
  const safeMenuId = ensureMenuId(menuId);
  const { data, error } = await menuQuery(supabase
    .from("subcategories")
    .select(lightweight ? "id, category_id, name, sort_order, created_at" : "*")
    .eq("menu_id", safeMenuId)
    .eq("active", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true }), signal);

  if (error) {
    throw normalizeError(error, "No se pudieron cargar las subcategorias.");
  }

  return data || [];
};

const listProducts = async (menuId, { includeInactive = false, includeImages = true, signal } = {}) => {
  const safeMenuId = ensureMenuId(menuId);
  let query = supabase
    .from("products")
    .select(includeImages ? "*" : "id, menu_id, category_id, subcategory_id, name, description, price_mode, price, suggested_price, active, visible_in_menu, pizza_size_options, empanada_sale_options, empanada_cooking_options, created_at")
    .eq("menu_id", safeMenuId)
    .order("created_at", { ascending: true });

  if (!includeInactive) {
    query = query.eq("active", true);
  }

  const { data, error } = await menuQuery(query, signal);

  if (error) {
    throw normalizeError(error, "No se pudieron cargar los platos del menu.");
  }

  return data || [];
};

export const listMenuProductChoices = async (menuId, categoryId, subcategoryId = "") => {
  ensureSupabaseReady();
  const safeMenuId = ensureMenuId(menuId);
  const safeCategoryId = String(categoryId || "").trim();
  const safeSubcategoryId = String(subcategoryId || "").trim();
  if (!safeCategoryId) return [];

  const products = [];
  const pageSize = 500;
  for (let offset = 0; ; offset += pageSize) {
    let query = supabase.from("products")
      .select("id, name, category_id, subcategory_id, active")
      .eq("menu_id", safeMenuId)
      .eq("category_id", safeCategoryId)
      .order("name", { ascending: true })
      .order("id", { ascending: true })
      .range(offset, offset + pageSize - 1);
    if (safeSubcategoryId) query = query.eq("subcategory_id", safeSubcategoryId);
    const { data, error } = await query;
    if (error) throw normalizeError(error, "No se pudieron cargar los platos de este grupo.");
    const rows = data || [];
    products.push(...rows.map((product) => ({
      id: product.id,
      nombre: product.name || "Plato sin nombre",
      categoria: product.category_id,
      subcategoriaId: product.subcategory_id || "",
      active: product.active !== false,
    })));
    if (rows.length < pageSize) return products;
  }
};

const listProductDeliverySettings = async (branchId, signal) => {
  const { data, error } = await menuQuery(supabase
    .from("external_product_channel_settings")
    .select("product_id, active, price_override")
    .eq("branch_id", branchId)
    .eq("channel_code", DELIVERY_MENU_CHANNEL), signal);

  if (error) {
    if (isMissingProductChannelSettingsTableError(error)) {
      return {
        rows: [],
        unavailable: true,
      };
    }

    throw normalizeError(
      error,
      "No se pudo cargar la disponibilidad de los platos para Delivery."
    );
  }

  return {
    rows: data || [],
    unavailable: false,
  };
};

const listMenuSubcategoryLinks = async (menuId, signal) => {
  const safeMenuId = ensureMenuId(menuId);
  const { data, error } = await menuQuery(supabase
    .from("menu_subcategory_links")
    .select("*")
    .eq("menu_id", safeMenuId)
    .eq("active", true)
    .order("created_at", { ascending: true }), signal);

  if (error) {
    if (isMissingRelationError(error, "menu_subcategory_links")) {
      return {
        rows: [],
        missingRelation: true,
      };
    }

    throw normalizeError(
      error,
      "No se pudieron cargar las asociaciones entre subcategorias."
    );
  }

  const ingredientIds = [...new Set((data || []).map((link) => link.target_ingredient_id).filter(Boolean))];
  let ingredients = [];
  if (ingredientIds.length) {
    const result = await menuQuery(supabase.from("ingredients").select("id,name,active").in("id", ingredientIds), signal);
    if (result.error) throw normalizeError(result.error, "No se pudieron cargar las variedades de bebida.");
    ingredients = result.data || [];
  }
  const ingredientById = new Map(ingredients.map((item) => [item.id, item]));
  return {
    rows: (data || []).map((link) => ({ ...link, targetIngredient: ingredientById.get(link.target_ingredient_id) })),
    missingRelation: false,
  };
};

const listProductIngredients = async (productIds, { signal, includeImages = true } = {}) => {
  if (!productIds.length) {
    return [];
  }

  const { data, error } = await menuQuery(supabase
    .from("product_ingredients")
    .select("*")
    .in("product_id", productIds), signal);

  if (error) {
    throw normalizeError(error, "No se pudieron cargar los ingredientes de los platos.");
  }

  const productIngredients = data || [];
  const ingredientIds = [
    ...new Set(
      productIngredients
        .map((item) => item.ingredient_id)
        .filter(Boolean)
    ),
  ];

  if (!ingredientIds.length) {
    return productIngredients;
  }

  const { data: ingredients, error: ingredientsError } = await menuQuery(supabase
    .from("ingredients")
    .select(includeImages ? "id, name, image_url, price, type, selectable, active" : "id, name, price, type, selectable, active")
    .in("id", ingredientIds), signal);

  if (ingredientsError) {
    throw normalizeError(
      ingredientsError,
      "No se pudieron cargar los detalles de los ingredientes del menu."
    );
  }

  const ingredientsById = new Map(
    (ingredients || [])
      .filter((ingredient) => ingredient?.active !== false)
      .map((ingredient) => [ingredient.id, ingredient])
  );

  return productIngredients.map((item) => ({
    ...item,
    ingredient: ingredientsById.get(item.ingredient_id) || null,
  }));
};

const ensureProductIngredientUnitsSupported = async () => {
  if (productIngredientUnitSupportChecked) {
    return;
  }

  const { error } = await supabase.from("product_ingredients").select("unit").limit(1);

  if (error) {
    if (isMissingColumnError(error, "unit")) {
      throw new Error(
        "Falta aplicar menu_recipe_stock_units_patch.sql en Supabase para guardar unidades de consumo por ingrediente."
      );
    }

    throw normalizeError(
      error,
      "No pudimos verificar las unidades de consumo de la receta."
    );
  }

  productIngredientUnitSupportChecked = true;
};

const ensureProductIngredientMenuQuantitiesSupported = async () => {
  if (productIngredientMenuQuantitySupportChecked) {
    return;
  }

  const { error } = await supabase
    .from("product_ingredients")
    .select("menu_quantity")
    .limit(1);

  if (error) {
    if (isMissingColumnError(error, "menu_quantity")) {
      throw new Error(
        "Falta aplicar product_recipe_customization_patch.sql en Supabase para separar stock y cantidad visible en el menu."
      );
    }

    throw normalizeError(
      error,
      "No pudimos verificar la cantidad visible de los ingredientes."
    );
  }

  productIngredientMenuQuantitySupportChecked = true;
};

const normalizeCategories = (categories) =>
  categories.map((category) => ({
    id: category.id,
    nombre: category.name || "",
    imageUrl: category.image_url || category.imageUrl || "",
    fotoUrl: category.image_url || category.imageUrl || "",
    icon: category.icon || buildCategoryIcon(category.name),
  }));

const normalizeSubcategories = (subcategories) =>
  subcategories.reduce((acc, subcategory) => {
    if (!acc[subcategory.category_id]) {
      acc[subcategory.category_id] = [];
    }

    acc[subcategory.category_id].push({
      id: subcategory.id,
      nombre: subcategory.name || "",
    });

    return acc;
  }, {});

const normalizeProductIngredient = (item) => {
  const ingredient = item?.ingredient || {};
  const consumptionQuantity = getIngredientConsumptionQuantity(item);
  const menuBaseQuantity = getIngredientMenuBaseQuantity(item);
  const maxExtraQuantity = Number(item?.max_extra_quantity || 0);
  const normalizedMaxExtraQuantity = item?.can_add_more
    ? Math.max(1, maxExtraQuantity || DEFAULT_MAX_EXTRA_QUANTITY)
    : 0;

  return {
    id: item?.ingredient_id || ingredient?.id || "",
    ingredientId: item?.ingredient_id || ingredient?.id || "",
    name: ingredient?.name || "",
    nombre: ingredient?.name || "",
    imageUrl: ingredient?.image_url || "",
    fotoUrl: ingredient?.image_url || "",
    price: Number(ingredient?.price || 0),
    precio: Number(ingredient?.price || 0),
    type: ingredient?.type || "optional",
    tipo: ingredient?.type || "optional",
    selectable: ingredient?.selectable !== false,
    cantidad: menuBaseQuantity,
    quantity: menuBaseQuantity,
    baseQuantity: menuBaseQuantity,
    menuBaseQuantity,
    menuQuantity: menuBaseQuantity,
    consumptionQuantity,
    stockQuantity: consumptionQuantity,
    consumptionBaseQuantity: consumptionQuantity,
    unit: normalizeStockUnit(item?.unit || item?.consumption_unit || DEFAULT_STOCK_UNIT),
    unidad: normalizeStockUnit(item?.unit || item?.consumption_unit || DEFAULT_STOCK_UNIT),
    consumptionUnit: normalizeStockUnit(
      item?.unit || item?.consumption_unit || DEFAULT_STOCK_UNIT
    ),
    canRemove: item?.can_remove === true,
    canAddMore: item?.can_add_more === true,
    maxExtraQuantity: normalizedMaxExtraQuantity,
  };
};

const normalizeProducts = (
  products,
  categories,
  subcategories,
  productIngredients,
  deliverySettings = []
) => {
  const categoryById = new Map(categories.map((item) => [item.id, item]));
  const subcategoryById = new Map(subcategories.map((item) => [item.id, item]));
  const deliverySettingByProductId = new Map(
    deliverySettings.map((item) => [item.product_id, item])
  );

  return products.map((product) => {
    const normalizedIngredients = productIngredients
      .filter((item) => item.product_id === product.id)
      .map(normalizeProductIngredient);
    const deliverySetting = deliverySettingByProductId.get(product.id);
    const pizzaSizeOptions = normalizePizzaSizeOptions(
      product.pizza_size_options || []
    );
    const empanadaSaleOptions = normalizeEmpanadaSaleOptions(
      product.empanada_sale_options || []
    );
    const empanadaCookingOptions = normalizeEmpanadaCookingOptions(
      product.empanada_cooking_options || []
    );

    return {
      id: product.id,
      active: product.active !== false,
      visibleInMenu: (product.visible_in_menu ?? product.active) !== false,
      deliveryAvailable: deliverySetting?.active !== false,
      deliveryPriceOverride:
        deliverySetting?.price_override === null ||
        deliverySetting?.price_override === undefined
          ? null
          : Number(deliverySetting.price_override),
      nombre: product.name || "",
      name: product.name || "",
      descripcion: product.description || "",
      description: product.description || "",
      foto: null,
      fotoUrl: product.image_url || null,
      imageUrl: product.image_url || null,
      ingredients: normalizedIngredients,
      ingredientes: normalizedIngredients.map((ingredient) => ({ ...ingredient })),
      categoria: product.category_id,
      categoryId: product.category_id,
      category_id: product.category_id,
      categoryName: categoryById.get(product.category_id)?.name || "",
      subcategoriaId: product.subcategory_id || "",
      subcategoryId: product.subcategory_id || "",
      subcategory_id: product.subcategory_id || "",
      subcategoria: subcategoryById.get(product.subcategory_id)?.name || "",
      subcategoryName: subcategoryById.get(product.subcategory_id)?.name || "",
      modoPrecio: normalizePriceMode(product.price_mode),
      priceMode: normalizePriceMode(product.price_mode),
      precio: Number(product.price || 0),
      price: Number(product.price || 0),
      precioSugerido: Number(product.suggested_price || 0),
      suggestedPrice: Number(product.suggested_price || 0),
      suggested_price: Number(product.suggested_price || 0),
      pizzaSizeOptions,
      pizza_size_options: pizzaSizeOptions,
      empanadaSaleOptions,
      empanada_sale_options: empanadaSaleOptions,
      empanadaCookingOptions,
      empanada_cooking_options: empanadaCookingOptions,
    };
  });
};

const normalizeSubcategoryLinks = (links, categories, subcategories, products) => {
  const categoryById = new Map(categories.map((item) => [item.id, item]));
  const subcategoryById = new Map(subcategories.map((item) => [item.id, item]));
  const productById = new Map(products.map((item) => [item.id, item]));

  return links.map((link) => ({
    id: link.id,
    sourceCategoryId: link.source_category_id || subcategoryById.get(link.source_subcategory_id)?.category_id || "",
    sourceCategoryName: categoryById.get(link.source_category_id || subcategoryById.get(link.source_subcategory_id)?.category_id)?.name || "",
    sourceProductId: link.source_product_id || "",
    sourceProductName: productById.get(link.source_product_id)?.name || "",
    sourceSubcategoryId: link.source_subcategory_id,
    sourceSubcategoryName:
      subcategoryById.get(link.source_subcategory_id)?.name || "",
    targetCategoryId:
      link.target_category_id ||
      subcategoryById.get(link.target_subcategory_id)?.category_id ||
      "",
    targetCategoryName:
      categoryById.get(
        link.target_category_id ||
          subcategoryById.get(link.target_subcategory_id)?.category_id
      )?.name || "",
    targetSubcategoryId: link.target_subcategory_id,
    targetSubcategoryName:
      subcategoryById.get(link.target_subcategory_id)?.name || "",
    targetProductId: link.target_product_id || "",
    targetProductName: productById.get(link.target_product_id)?.name || "",
    targetIngredientId: link.target_ingredient_id || "",
    targetIngredientName: link.targetIngredient?.name || "",
    targetIngredientActive: link.targetIngredient?.active !== false,
    consumptionMl: link.consumption_ml == null ? null : Number(link.consumption_ml),
    showAsStandalone: link.show_as_standalone === true,
    title: link.title || "",
    selectionMode: normalizeLinkSelectionMode(link.selection_mode),
    active: link.active !== false,
  }));
};

const findCategoryByName = async (menuId, name) => {
  const categories = await listCategories(menuId);
  return (
    categories.find(
      (category) => category.name?.toLowerCase() === name.trim().toLowerCase()
    ) || null
  );
};

const findSubcategoryByName = async (menuId, categoryId, name) => {
  const subcategories = await listSubcategories(menuId);
  return (
    subcategories.find(
      (subcategory) =>
        subcategory.category_id === categoryId &&
        subcategory.name?.toLowerCase() === name.trim().toLowerCase()
    ) || null
  );
};

// The style editor only needs category names; do not load photos or seed a menu here.
export const getBranchMenuCategories = async (branchId, { includeSubcategories = false } = {}) => {
  ensureSupabaseReady();
  if (!branchId) throw new Error("No encontramos una sucursal activa.");
  return withRequestDeadline(async (signal) => {
    const { data, error } = await menuQuery(supabase
      .from("branch_menu_assignments").select("menu_id")
      .eq("branch_id", branchId).maybeSingle(), signal);
    if (error) throw normalizeError(error, "No se pudo cargar el menu asignado.");
    if (!data?.menu_id) return [];
    const categories = normalizeCategories(await listCategories(data.menu_id, signal, false));
    if (!includeSubcategories) return categories;
    const subcategories = normalizeSubcategories(await listSubcategories(data.menu_id, signal, true));
    return categories.map((category) => ({ ...category, subcategories: subcategories[category.id] || [] }));
  }, { timeoutMs: 15000, message: "La carga de categorias tardo demasiado. Vuelve a intentar." });
};

export const getBranchMenuSnapshot = async (
  branchId,
  { includeInactiveProducts = false, includeImages = true } = {}
) => {
  ensureSupabaseReady();

  if (!branchId) {
    throw new Error("No encontramos una sucursal activa para cargar el menu.");
  }

  const load = async (signal) => {
  const assignment = await getAssignedMenu(branchId, signal);
  const [
    { categories, subcategories },
    { products, productIngredients },
    subcategoryLinksResult,
    deliverySettingsResult,
  ] = await Promise.all([
    // Categories may seed subcategories for a new menu; preserve that ordering.
    ensureDefaultCategories(assignment.menu_id, signal, includeImages).then(async (categories) => ({
      categories, subcategories: await listSubcategories(assignment.menu_id, signal),
    })),
    listProducts(assignment.menu_id, {
      includeInactive: includeInactiveProducts,
      includeImages, signal,
    }).then(async (products) => ({
      products,
      productIngredients: await listProductIngredients(products.map((item) => item.id), { signal, includeImages }),
    })),
    listMenuSubcategoryLinks(assignment.menu_id, signal),
    listProductDeliverySettings(branchId, signal),
  ]);

  return {
    menuId: assignment.menu_id,
    mode: assignment.mode || "shared",
    categories: normalizeCategories(categories),
    subcategoriasPorCategoria: normalizeSubcategories(subcategories),
    linkedSubcategoryRules: normalizeSubcategoryLinks(
      subcategoryLinksResult.rows,
      categories,
      subcategories,
      products
    ),
    linkedRulesUnavailable: subcategoryLinksResult.missingRelation === true,
    deliverySettingsUnavailable: deliverySettingsResult.unavailable === true,
    products: normalizeProducts(
      products,
      categories,
      subcategories,
      productIngredients,
      deliverySettingsResult.rows
    ).map((product, index) => includeImages ? product : ({
      ...product, imageDeferred: true, imageMenuId: assignment.menu_id,
      imageVersion: products[index].created_at || "",
    })),
  };
  };
  return includeImages ? load() : withRequestDeadline(load, {
    timeoutMs: 15000,
    message: "La actualizacion de la carta tardo demasiado. Puedes reintentar sin volver a cargar las fotos.",
  });
};

export const getBranchMenuSetup = async (branchId) => {
  ensureSupabaseReady();

  if (!branchId) {
    throw new Error("No encontramos una sucursal para revisar su menu.");
  }

  const branch = await getBranchById(branchId);
  const assignment = await getAssignedMenu(branchId);
  const currentMenu = await getMenuById(assignment.menu_id);
  const sharedMenu = await getSharedCompanyMenu(branch.company_id);

  return {
    branchId: branch.id,
    branchName: branch.name || "",
    mode: assignment.mode || "shared",
    currentMenuId: currentMenu.id,
    currentMenuName: currentMenu.name || "Menu sin nombre",
    currentMenuScope: currentMenu.scope || "shared",
    isUsingSharedMenu:
      (assignment.mode || "shared") === "shared" ||
      currentMenu.scope === "shared",
    sharedMenuId: sharedMenu.id,
    sharedMenuName: sharedMenu.name || "Menu compartido",
  };
};

export const getBranchesMenuSetup = async (branchIds) => {
  ensureSupabaseReady();

  if (!branchIds?.length) {
    return [];
  }

  return Promise.all(branchIds.map((branchId) => getBranchMenuSetup(branchId)));
};

export const assignSharedMenuToBranch = async (branchId) => {
  ensureSupabaseReady();
  const { error } = await supabase.rpc("assign_shared_menu_to_branch", {
    p_branch_id: branchId,
  });

  if (error) {
    throw normalizeError(error, "No se pudo volver al menu compartido.");
  }

  return getBranchMenuSetup(branchId);
};

export const createCustomMenuForBranch = async (
  branchId,
  { copyFromCurrent = true, createEmpty = false } = {}
) => {
  ensureSupabaseReady();
  const shouldCopy = copyFromCurrent && !createEmpty;
  const { error } = await supabase.rpc("create_custom_menu_for_branch", {
    p_branch_id: branchId,
    p_copy_from_current: shouldCopy,
  });

  if (error) {
    if (isMenuNameConstraintError(error)) {
      throw new Error(
        "La base todavia esta creando menus propios con nombres repetidos. Corre menu_custom_name_collision_patch.sql en Supabase y vuelve a intentar."
      );
    }
    throw normalizeError(error, "No se pudo crear el menu propio de la sucursal.");
  }

  return getBranchMenuSetup(branchId);
};

export const createMenuCategory = async (menuId, { name, icon }) => {
  ensureSupabaseReady();
  const safeMenuId = ensureMenuId(menuId);

  const trimmedName = name?.trim();
  if (!trimmedName) {
    throw new Error("El nombre de la categoria es obligatorio.");
  }

  const existing = await findCategoryByName(safeMenuId, trimmedName);
  if (existing) {
    return {
      id: existing.id,
      nombre: existing.name,
      icon: existing.icon || buildCategoryIcon(existing.name),
    };
  }

  const currentCategories = await listCategories(safeMenuId);
  const { data, error } = await supabase
    .from("categories")
    .insert({
      menu_id: safeMenuId,
      name: trimmedName,
      icon: icon || buildCategoryIcon(trimmedName),
      sort_order: currentCategories.length,
      active: true,
    })
    .select("*")
    .single();

  if (error) {
    throw normalizeError(error, "No se pudo crear la categoria.");
  }

  return {
    id: data.id,
    nombre: data.name,
    icon: data.icon || buildCategoryIcon(data.name),
  };
};

export const deleteMenuCategory = async (menuId, categoryId) => {
  ensureSupabaseReady();
  const safeMenuId = ensureMenuId(menuId);

  const { error } = await supabase
    .from("categories")
    .delete()
    .eq("menu_id", safeMenuId)
    .eq("id", categoryId);

  if (error) {
    throw normalizeError(error, "No se pudo eliminar la categoria.");
  }
};

export const createMenuSubcategory = async (menuId, categoryId, name) => {
  ensureSupabaseReady();
  const safeMenuId = ensureMenuId(menuId);

  const trimmedName = name?.trim();
  if (!trimmedName) {
    throw new Error("El nombre de la subcategoria es obligatorio.");
  }

  const existing = await findSubcategoryByName(safeMenuId, categoryId, trimmedName);
  if (existing) {
    return {
      id: existing.id,
      nombre: existing.name,
    };
  }

  const currentSubcategories = await listSubcategories(safeMenuId);
  const nextSortOrder = currentSubcategories.filter(
    (subcategory) => subcategory.category_id === categoryId
  ).length;

  const { data, error } = await supabase
    .from("subcategories")
    .insert({
      menu_id: safeMenuId,
      category_id: categoryId,
      name: trimmedName,
      sort_order: nextSortOrder,
      active: true,
    })
    .select("*")
    .single();

  if (error) {
    throw normalizeError(error, "No se pudo crear la subcategoria.");
  }

  return {
    id: data.id,
    nombre: data.name,
  };
};

export const deleteMenuSubcategory = async (menuId, categoryId, subcategoryId) => {
  ensureSupabaseReady();
  const safeMenuId = ensureMenuId(menuId);

  const { error } = await supabase
    .from("subcategories")
    .delete()
    .eq("menu_id", safeMenuId)
    .eq("category_id", categoryId)
    .eq("id", subcategoryId);

  if (error) {
    throw normalizeError(error, "No se pudo eliminar la subcategoria.");
  }
};

export const saveMenuProduct = async (menuId, payload) => {
  ensureSupabaseReady();
  const safeMenuId = ensureMenuId(menuId);
  let productId = payload.id;
  const recipePayload = (payload.ingredientes || [])
    .filter(
      (ingredient) => {
        const menuBaseQuantity = Number(
          ingredient.menuBaseQuantity ??
            ingredient.baseQuantity ??
            ingredient.cantidad ??
            0
        );
        const consumptionQuantity = Number(
          ingredient.consumptionQuantity ??
            ingredient.stockQuantity ??
            ingredient.quantity ??
            0
        );

        return (
          ingredient.id &&
          (menuBaseQuantity > 0 || Boolean(ingredient.canAddMore)) &&
          consumptionQuantity > 0
        );
      }
    )
    .map((ingredient) => ({
      product_id: "",
      ingredient_id: ingredient.id,
      quantity: Number(
        ingredient.consumptionQuantity ??
          ingredient.stockQuantity ??
          ingredient.quantity ??
          0
      ),
      menu_quantity: Number(
        ingredient.menuBaseQuantity ??
          ingredient.baseQuantity ??
          ingredient.cantidad ??
          0
      ),
      unit: normalizeStockUnit(
        ingredient.consumptionUnit ?? ingredient.unit ?? ingredient.unidad
      ),
      can_remove:
        Number(
          ingredient.menuBaseQuantity ??
            ingredient.baseQuantity ??
            ingredient.cantidad ??
            0
        ) > 0 &&
        Boolean(ingredient.canRemove),
      can_add_more: Boolean(ingredient.canAddMore),
      max_extra_quantity: Boolean(ingredient.canAddMore)
        ? Math.max(
            1,
            Number(ingredient.maxExtraQuantity ?? DEFAULT_MAX_EXTRA_QUANTITY)
          )
        : 0,
    }));

  if (recipePayload.length > 0) {
    await ensureProductIngredientUnitsSupported();
    await ensureProductIngredientMenuQuantitiesSupported();
  }

  const shouldWriteImage =
    !productId ||
    payload.imageChanged === true ||
    payload.removeImage === true ||
    payload.foto instanceof File;
  const imageUrl = shouldWriteImage
    ? payload.foto instanceof File
      ? await fileToDataUrl(payload.foto)
      : payload.fotoUrl || null
    : undefined;

  const basePayload = {
    menu_id: safeMenuId,
    category_id: payload.categoria,
    subcategory_id: payload.subcategoriaId || null,
    name: payload.nombre?.trim() || "",
    description: payload.descripcion?.trim() || null,
    price_mode: normalizePriceMode(payload.modoPrecio),
    price: Number(payload.precio || 0),
    suggested_price: Number(payload.precioSugerido || 0),
    active: payload.active !== false,
  };
  const hasPizzaSizeOptionsPayload =
    Object.prototype.hasOwnProperty.call(payload, "pizzaSizeOptions") ||
    Object.prototype.hasOwnProperty.call(payload, "pizza_size_options");

  if (hasPizzaSizeOptionsPayload) {
    await ensureProductPizzaSizeOptionsSupported();
    basePayload.pizza_size_options = normalizePizzaSizeOptions(
      payload.pizzaSizeOptions || payload.pizza_size_options || []
    );
  }

  const hasEmpanadaSaleOptionsPayload =
    Object.prototype.hasOwnProperty.call(payload, "empanadaSaleOptions") ||
    Object.prototype.hasOwnProperty.call(payload, "empanada_sale_options");

  if (hasEmpanadaSaleOptionsPayload) {
    await ensureProductEmpanadaSaleOptionsSupported();
    basePayload.empanada_sale_options = normalizeEmpanadaSaleOptions(
      payload.empanadaSaleOptions || payload.empanada_sale_options || []
    );
  }

  const hasEmpanadaCookingOptionsPayload =
    Object.prototype.hasOwnProperty.call(payload, "empanadaCookingOptions") ||
    Object.prototype.hasOwnProperty.call(payload, "empanada_cooking_options");

  if (hasEmpanadaCookingOptionsPayload) {
    await ensureProductEmpanadaCookingOptionsSupported();
    basePayload.empanada_cooking_options = normalizeEmpanadaCookingOptions(
      payload.empanadaCookingOptions || payload.empanada_cooking_options || []
    );
  }

  if (shouldWriteImage) {
    basePayload.image_url = imageUrl;
  }

  // Editing a recipe must not make a hidden product visible again.
  if (typeof payload.visibleInMenu === "boolean") {
    basePayload.visible_in_menu = payload.visibleInMenu;
  }

  let currentRecipeRows = [];

  if (productId) {
    const { data, error } = await supabase
      .from("product_ingredients")
      .select("*")
      .eq("product_id", productId);

    if (error) {
      throw normalizeError(error, "No se pudo revisar la receta actual del plato.");
    }

    currentRecipeRows = data || [];
  }

  if (productId) {
    const { error } = await supabase
      .from("products")
      .update(basePayload)
      .eq("menu_id", safeMenuId)
      .eq("id", productId);

    if (error) {
      throw normalizeError(error, "No se pudo actualizar el plato.");
    }
  } else {
    const { data, error } = await supabase
      .from("products")
      .insert(basePayload)
      .select("id")
      .single();

    if (error) {
      throw normalizeError(error, "No se pudo guardar el plato.");
    }

    productId = data.id;
  }

  if (shouldWriteImage) invalidateMenuProductImage(safeMenuId, productId);

  const ingredientPayload = recipePayload.map((ingredient) => ({
    ...ingredient,
    product_id: productId,
  }));
  const currentRecipeByIngredientId = new Map(
    currentRecipeRows.map((row) => [row.ingredient_id, row])
  );
  const nextIngredientIds = new Set(
    ingredientPayload.map((row) => row.ingredient_id)
  );
  const changedRecipeRows = ingredientPayload.filter(
    (row) => !recipeRowsMatch(currentRecipeByIngredientId.get(row.ingredient_id), row)
  );
  const removedIngredientIds = currentRecipeRows
    .map((row) => row.ingredient_id)
    .filter((ingredientId) => !nextIngredientIds.has(ingredientId));

  await upsertChangedRecipeRows(changedRecipeRows);
  await deleteRemovedRecipeRows(productId, removedIngredientIds);

  return productId;
};

export const updateMenuProductPresentation = async (menuId, productId, changes = {}) => {
  ensureSupabaseReady();
  const safeMenuId = ensureMenuId(menuId);
  if (!productId) throw new Error("No encontramos el plato a actualizar.");
  const update = {};
  if (typeof changes.active === "boolean") update.active = changes.active;
  if (typeof changes.visibleInMenu === "boolean") update.visible_in_menu = changes.visibleInMenu;
  if (!Object.keys(update).length) throw new Error("No hay cambios de visibilidad o disponibilidad.");
  const { data, error } = await supabase.from("products").update(update)
    .eq("menu_id", safeMenuId).eq("id", productId)
    .select("id, active, visible_in_menu").single();
  if (error) {
    if (isMissingColumnError(error, "visible_in_menu")) {
      throw new Error("Falta actualizar la visibilidad de platos en Supabase. No se guardo el cambio.");
    }
    throw normalizeError(error, "No se pudo actualizar la visibilidad del plato.");
  }
  if (!data) throw new Error("No se pudo confirmar el cambio del plato.");
  return { id: data.id, active: data.active !== false, visibleInMenu: data.visible_in_menu !== false };
};

export const saveMenuProductsDeliveryAvailability = async ({
  branchId,
  changes = [],
}) => {
  ensureSupabaseReady();

  if (!branchId) {
    throw new Error(
      "No encontramos la sucursal para actualizar los platos de Delivery."
    );
  }

  const changesByProductId = new Map();

  (Array.isArray(changes) ? changes : []).forEach((change) => {
    const productId = String(change?.productId || "").trim();

    if (productId) {
      changesByProductId.set(productId, change?.active === true);
    }
  });

  if (changesByProductId.size === 0) {
    return [];
  }

  const updatedAt = new Date().toISOString();
  const payload = Array.from(changesByProductId.entries()).map(
    ([productId, active]) => ({
      branch_id: branchId,
      product_id: productId,
      channel_code: DELIVERY_MENU_CHANNEL,
      active,
      updated_at: updatedAt,
    })
  );

  const { data, error } = await supabase
    .from("external_product_channel_settings")
    .upsert(payload, { onConflict: "branch_id,product_id,channel_code" })
    .select("product_id, active, price_override");

  if (error) {
    if (isMissingProductChannelSettingsTableError(error)) {
      throw new Error(
        "Falta aplicar external_orders_phase1_patch.sql en Supabase para configurar platos de Delivery."
      );
    }

    throw normalizeError(
      error,
      "No se pudo actualizar la disponibilidad de los platos para Delivery."
    );
  }

  return data || [];
};

export const saveMenuProductDeliveryAvailability = async ({
  branchId,
  productId,
  active,
}) => {
  const [savedSetting] = await saveMenuProductsDeliveryAvailability({
    branchId,
    changes: [{ productId, active }],
  });

  return savedSetting || null;
};

export const saveMenuSubcategoryLink = async (menuId, payload) => {
  ensureSupabaseReady();
  const safeMenuId = ensureMenuId(menuId);

  const basePayload = {
    menu_id: safeMenuId,
    source_category_id: payload.sourceCategoryId || null,
    source_subcategory_id: payload.sourceSubcategoryId || null,
    source_product_id: payload.sourceProductId || null,
    target_category_id: payload.targetCategoryId || null,
    target_subcategory_id: payload.targetSubcategoryId || null,
    target_product_id: payload.targetProductId || null,
    target_ingredient_id: payload.targetIngredientId || null,
    consumption_ml: payload.targetIngredientId ? Number(payload.consumptionMl) : null,
    title: payload.title?.trim() || null,
    selection_mode: normalizeLinkSelectionMode(payload.selectionMode),
    show_as_standalone: false,
    active: true,
  };

  const query = payload.id
    ? supabase
        .from("menu_subcategory_links")
        .update(basePayload)
        .eq("menu_id", safeMenuId)
        .eq("id", payload.id)
    : supabase.from("menu_subcategory_links").insert(basePayload);
  const { error } = await query;

  if (error) {
    if (["42703", "PGRST204"].includes(error.code) &&
        ["target_ingredient_id", "consumption_ml"].some((column) => isMissingColumnError(error, column))) {
      throw new Error("Falta actualizar las relaciones de bebidas con beverage_ingredient_links_patch.sql. No se guardo la asociacion.");
    }
    if (["42703", "PGRST204"].includes(error.code) &&
        ["source_category_id", "source_product_id", "target_product_id"].some((column) => isMissingColumnError(error, column))) {
      throw new Error("Falta actualizar las relaciones del menu en Supabase con menu_product_links_patch.sql. No se guardo la asociacion.");
    }
    if (
      ["42P01", "PGRST205"].includes(error.code) ||
      (["42703", "PGRST204"].includes(error.code) && isMissingColumnError(error, "target_category_id"))
    ) {
      throw new Error(
        "Falta la tabla de asociaciones guiadas en Supabase. Corre menu_subcategory_links_patch.sql para persistirlas en la base."
      );
    }

    throw normalizeError(
      error,
      "No se pudo guardar la asociacion entre subcategorias."
    );
  }
};

export const deleteMenuSubcategoryLink = async (menuId, ruleId) => {
  ensureSupabaseReady();
  const safeMenuId = ensureMenuId(menuId);

  const { error } = await supabase
    .from("menu_subcategory_links")
    .delete()
    .eq("menu_id", safeMenuId)
    .eq("id", ruleId);

  if (error) {
    if (isMissingRelationError(error, "menu_subcategory_links")) {
      throw new Error(
        "Falta la tabla de asociaciones guiadas en Supabase. Corre menu_subcategory_links_patch.sql para poder eliminarlas tambien en la base."
      );
    }

    throw normalizeError(
      error,
      "No se pudo eliminar la asociacion entre subcategorias."
    );
  }
};

export const deleteMenuProduct = async (menuId, productId) => {
  ensureSupabaseReady();
  const safeMenuId = ensureMenuId(menuId);

  const { error } = await supabase
    .from("products")
    .delete()
    .eq("menu_id", safeMenuId)
    .eq("id", productId);

  if (error) {
    throw normalizeError(error, "No se pudo eliminar el plato.");
  }
};
